// Connect a brokerage account via Plaid and show its margin + account info.
// Requirement 8 (ARV sourced from the brokerage), 15 (Plaid link).
//
// The full Plaid Link handoff uses Plaid's hosted Link SDK: the backend mints a
// link_token, the SDK opens the institution login UI, and on success returns a
// public_token which we exchange server-side for a persisted item.
//
// The Link SDK (react-plaid-link) is not yet a dependency, so this page performs the two
// server calls directly and accepts the public_token manually. Wiring the SDK is a TODO:
// install react-plaid-link, pass link_token to usePlaidLink, and call exchange in onSuccess.
//
// Once linked, the page loads the account summary so the operator can see the brokerage's
// equity, margin balance, and available buying power that back the Account Reference Value.

import { useCallback, useEffect, useState } from "react";

import { ApiClientError } from "../api/client";
import { useApi } from "../api/useApi";
import { timeAgo } from "../lib/format";
import type {
  AccountHistoryPoint,
  BrokerageAccountLine,
  BrokerageAccountSummary,
  ManualAccountOverride,
} from "../types";
import {
  AccountRelationshipBars,
  AccountRelationshipBarsSkeleton,
} from "../components/AccountRelationshipBars";
import { BrokerageHistoryChart } from "../components/BrokerageHistoryChart";
import { HoldingsSection } from "../components/HoldingsSection";
import { PortfolioBenchmarks } from "../components/PortfolioBenchmarks";
import { PlaidConnect } from "../components/PlaidConnect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function money(value: number | undefined | null, currency = "USD"): string {
  if (value === undefined || value === null) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
}

function percent(value: number | undefined | null): string {
  if (value === undefined || value === null) return "—";
  return `${value.toFixed(2)}%`;
}

type AccountGroup = {
  key: string;
  label: string;
  accounts: BrokerageAccountLine[];
  showCurrent: boolean;
  showAvailable: boolean;
  showMargin: boolean;
};

function currentDuplicatesMargin(account: BrokerageAccountLine): boolean {
  return account.margin_balance != null &&
    Math.abs(Math.abs(account.current_value) - account.margin_balance) < 0.005;
}

function accountGroupLabel(account: BrokerageAccountLine): string {
  const value = (account.subtype || account.account_type || "Other").trim();
  const knownLabels: Record<string, string> = {
    hsa: "HSA",
    ira: "IRA",
    "roth 401k": "Roth 401(k)",
    "roth ira": "Roth IRA",
    "401k": "401(k)",
  };
  return knownLabels[value.toLowerCase()] ?? value.replace(/(^|[\s_-])\S/g, (letter) => letter.toUpperCase());
}

function groupAccounts(accounts: BrokerageAccountLine[]): AccountGroup[] {
  const groups = new Map<string, BrokerageAccountLine[]>();
  for (const account of accounts) {
    const key = (account.subtype || account.account_type || "other").trim().toLowerCase();
    groups.set(key, [...(groups.get(key) ?? []), account]);
  }
  return [...groups.entries()].map(([key, grouped]) => ({
    key,
    label: accountGroupLabel(grouped[0]),
    accounts: grouped,
    showCurrent: grouped.some((account) => !currentDuplicatesMargin(account)),
    showAvailable: grouped.some((account) => account.available_value != null),
    showMargin: grouped.some((account) => account.margin_balance != null),
  }));
}

function ManualValuesSkeleton() {
  return (
    <div className="space-y-4" data-testid="manual-values-skeleton" aria-busy="true">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-4 w-64 max-w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-3 w-full max-w-lg" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-9 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
      <Skeleton className="h-9 w-40" />
    </div>
  );
}

function BrokerageDataSkeleton() {
  return (
    <div className="space-y-6" data-testid="brokerage-data-skeleton" aria-busy="true">
      <Card className="gap-3 py-3">
        <CardHeader className="space-y-2 py-0">
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
      <Card className="gap-3 py-3">
        <CardHeader className="space-y-2 py-0">
          <Skeleton className="h-6 w-44" />
          <Skeleton className="h-3 w-24" />
        </CardHeader>
        <CardContent className="space-y-4">
          <AccountRelationshipBarsSkeleton />
          <div className="space-y-2 rounded-md border border-border p-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
          <div className="space-y-3">
            <Skeleton className="h-4 w-20" />
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-start">
              <Skeleton className="size-[240px] shrink-0 rounded-full" />
              <div className="grid w-full flex-1 grid-cols-1 gap-2 sm:grid-cols-2">
                {Array.from({ length: 6 }).map((_, index) => (
                  <Skeleton key={index} className="h-4 w-full" />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function LinkBrokerage() {
  const api = useApi();
  const [summary, setSummary] = useState<BrokerageAccountSummary | null>(null);
  const [history, setHistory] = useState<AccountHistoryPoint[]>([]);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  // True until the first account fetch resolves, so the page shows a skeleton instead of
  // flashing the "not connected" form while the (possibly slow) request is in flight.
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [manualLoading, setManualLoading] = useState(true);
  // True only while a user-triggered "Refresh accounts" is in flight, so the holdings
  // detail shows a skeleton instead of sitting on stale figures.
  const [refreshing, setRefreshing] = useState(false);
  // When already linked, the consent + connect controls are hidden to avoid implying the
  // user still needs to act. This flips true only when they explicitly choose to
  // reconnect or switch brokerage, which reveals the connect flow again.
  const [reconnecting, setReconnecting] = useState(false);
  const [manual, setManual] = useState<ManualAccountOverride>({
    enabled: false,
    prefer_plaid: true,
    account_value: null,
    margin_balance: null,
    marginable_equity_only: true,
  });
  const [manualStatus, setManualStatus] = useState("");
  // Explicit consent is required before initiating the Plaid link (data collection).
  const [consented, setConsented] = useState(false);

  const loadAccounts = useCallback(async () => {
    let loaded = false;
    try {
      const data = await api.getBrokerageAccounts();
      setSummary(data);
      loaded = true;
      setAccountsError(null);
    } catch (err) {
      setSummary(null);
      // A 404 means genuinely no linked item / no manual override: the clean "connect"
      // state. Any other error (e.g. the balance read failed) means we could not load an
      // account that may well be linked, so surface that rather than silently implying
      // the user is not connected.
      const notFound = err instanceof ApiClientError && err.status === 404;
      setAccountsError(notFound ? null : "Couldn't load your linked account. Try again shortly.");
    } finally {
      // Current balances can render immediately; account history is a separate, optional
      // section and must not hold the entire brokerage view behind its skeleton.
      setAccountsLoading(false);
    }

    if (loaded) {
      try {
        setHistory((await api.getBrokerageAccountHistory()).points);
      } catch {
        // Current balances remain useful if older observations cannot be loaded.
      }
    }
  }, [api]);

  const loadManual = useCallback(async () => {
    try {
      setManual(await api.getManualAccount());
    } catch {
      // Keep defaults on failure.
    } finally {
      setManualLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadAccounts();
    void loadManual();
  }, [loadAccounts, loadManual]);

  const saveManual = async () => {
    setManualStatus("");
    try {
      const saved = await api.updateManualAccount(manual);
      setManual(saved);
      setManualStatus("Manual values saved.");
      await loadAccounts();
    } catch (err) {
      setManualStatus(err instanceof Error ? err.message : "Save failed");
    }
  };

  const patchManual = (next: Partial<ManualAccountOverride>) =>
    setManual((prev) => ({ ...prev, ...next }));

  const numOrNull = (v: string): number | null => (v === "" ? null : Number(v));

  const onLinked = () => {
    // PlaidConnect already shows the "Linked ..." confirmation; here we just refresh the
    // account summary so the newly linked balances appear.
    void loadAccounts();
  };

  // Whether a live Plaid item is backing the summary. This is real backend state (the
  // accounts summary came from Plaid), not the transient post-connect message, so the
  // connected banner stays accurate across reloads.
  const plaidLinked = summary?.source === "plaid";

  return (
    <section className="max-w-3xl space-y-6">
      {/* The nav names this page ("Brokerage") and the card below states the connected
          status and purpose, so no separate page title/intro is needed. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-lg">
            Connect via Plaid
            {plaidLinked && (
              <Badge
                variant="secondary"
                className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                data-testid="plaid-linked-pill"
              >
                Connected
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Read balances and margin to calculate account value and LTV.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {accountsLoading && (
            <div className="space-y-3" data-testid="brokerage-loading">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          )}
          {!accountsLoading && plaidLinked && (
            <div
              className="flex items-center gap-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3"
              data-testid="plaid-linked-banner"
            >
              <span
                className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400"
                aria-hidden="true"
              >
                {/* check mark */}
                <svg viewBox="0 0 20 20" fill="currentColor" className="size-4">
                  <path
                    fillRule="evenodd"
                    d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.3 3.29 6.8-6.8a1 1 0 011.4 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  Connected to {summary?.institution_name ?? "your brokerage"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Smart Margin is reading your balances and margin from this account via
                  Plaid.
                </p>
              </div>
              <Badge variant="secondary" className="ml-auto shrink-0">
                Linked
              </Badge>
            </div>
          )}
          {!accountsLoading && accountsError && !plaidLinked && (
            <p
              className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-400"
              data-testid="accounts-error"
            >
              {accountsError}
            </p>
          )}
          {/* Once linked, hide the consent + connect controls (they imply unfinished
              setup). Offer a quiet reconnect path for switching or re-linking. */}
          {accountsLoading ? null : plaidLinked && !reconnecting ? (
            <button
              type="button"
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              onClick={() => setReconnecting(true)}
              data-testid="reconnect-plaid"
            >
              Reconnect or switch brokerage
            </button>
          ) : (
            <>
          <label className="flex items-start gap-2 text-sm" data-testid="plaid-consent">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              aria-label="plaid-consent"
            />
            <span className="text-muted-foreground">
              I consent to smart-margin collecting, processing, and storing my brokerage
              account data via Plaid to provide margin monitoring, as described in the{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <PlaidConnect
                consented={consented}
                onLinked={() => {
                  setReconnecting(false);
                  onLinked();
                }}
              />
              {plaidLinked && reconnecting && (
                <button
                  type="button"
                  className="text-sm text-muted-foreground hover:text-foreground hover:underline"
                  onClick={() => setReconnecting(false)}
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card className="manual-override" data-testid="manual-override">
        <CardHeader>
          <CardTitle className="text-lg">Manual account values</CardTitle>
          <CardDescription>
            Enter values manually and choose whether Plaid overrides them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {manualLoading ? (
            <ManualValuesSkeleton />
          ) : (
          <>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={manual.enabled}
              onChange={(e) => patchManual({ enabled: e.target.checked })}
              aria-label="manual-enabled"
            />
            Use these manual values
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="size-4 accent-primary"
              checked={manual.prefer_plaid}
              onChange={(e) => patchManual({ prefer_plaid: e.target.checked })}
              aria-label="prefer-plaid"
            />
            Prefer Plaid when connected (uncheck to always use the manual values)
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0 accent-primary"
              checked={manual.marginable_equity_only}
              onChange={(e) => patchManual({ marginable_equity_only: e.target.checked })}
              aria-label="marginable-equity-only"
            />
            <span>
              Measure LTV against marginable equity only
              <span className="block text-muted-foreground">
                Count only taxable brokerage collateral, excluding retirement accounts a
                broker cannot lend against. Uncheck to measure against all investment
                equity.
              </span>
            </span>
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="manual-account-value">Account value</Label>
              <Input
                id="manual-account-value"
                type="number"
                step="0.01"
                value={manual.account_value ?? ""}
                onChange={(e) => patchManual({ account_value: numOrNull(e.target.value) })}
                aria-label="manual-account-value"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manual-margin-balance">Margin balance</Label>
              <Input
                id="manual-margin-balance"
                type="number"
                step="0.01"
                value={manual.margin_balance ?? ""}
                onChange={(e) => patchManual({ margin_balance: numOrNull(e.target.value) })}
                aria-label="manual-margin-balance"
              />
            </div>
          </div>
          <Button type="button" onClick={() => void saveManual()}>
            Save manual values
          </Button>
          {manualStatus && (
            <p className="status text-sm text-muted-foreground">{manualStatus}</p>
          )}
          </>
          )}
        </CardContent>
      </Card>

      {accountsLoading && <BrokerageDataSkeleton />}

      {history.length > 0 && <BrokerageHistoryChart points={history} />}

      {summary && (
        <Card className="brokerage-summary gap-3 py-3" data-testid="brokerage-summary">
          <CardHeader className="py-0">
            <CardTitle className="flex items-center gap-2 text-lg">
              {summary.institution_name ?? "Linked brokerage"}
              {summary.source && (
                <Badge
                  variant="secondary"
                  className="source-badge"
                  data-testid="summary-source"
                >
                  {summary.source === "manual" ? "manual entry" : "via Plaid"}
                </Badge>
              )}
            </CardTitle>
            {summary.as_of && (
              <p className="text-xs text-muted-foreground" data-testid="accounts-as-of">
                As of {timeAgo(summary.as_of)}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <AccountRelationshipBars summary={summary} />

            {summary.source === "plaid" && (
              <p className="ltv-basis-note text-sm text-muted-foreground">
                {summary.marginable_only
                  ? "LTV base: marginable equity only; retirement accounts excluded."
                  : "LTV base: all investment equity."}
              </p>
            )}

            {summary.source === "plaid" &&
              summary.margin_balance != null &&
              !summary.margin_balance_is_exact && (
                <p className="margin-note text-sm text-muted-foreground">
                  Your brokerage does not report an exact margin loan to Plaid, so this is
                  estimated from buying power minus equity.
                </p>
              )}

            <div className="divide-y divide-border rounded-md border border-border">
              {groupAccounts(summary.accounts).map((group) => (
                <section key={group.key} data-testid="account-group">
                  <h4 className="bg-muted/30 px-3 py-2 text-xs font-semibold text-muted-foreground">
                    {group.label}
                  </h4>
                  <Table className="block md:table md:table-fixed">
                    <colgroup className="hidden md:table-column-group">
                      <col className="w-[34%]" />
                      <col className="w-[22%]" />
                      <col className="w-[22%]" />
                      <col className="w-[22%]" />
                    </colgroup>
                    <TableHeader className="hidden md:table-header-group">
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead className="text-right">
                          {group.showCurrent ? "Current value" : null}
                        </TableHead>
                        <TableHead className="text-right">
                          {group.showAvailable ? "Available" : null}
                        </TableHead>
                        <TableHead className="text-right">
                          {group.showMargin ? "Margin" : null}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="block divide-y divide-border md:table-row-group">
                      {group.accounts.map((a) => (
                        <TableRow
                          key={a.account_id}
                          data-testid="account-row"
                          className="grid grid-cols-2 gap-x-4 gap-y-3 p-3 md:table-row md:p-0"
                        >
                          <TableCell className="col-span-2 block p-0 font-medium md:table-cell md:p-2">
                            {a.name}
                          </TableCell>
                          {group.showCurrent ? (
                            <TableCell
                              className={`${currentDuplicatesMargin(a) ? "hidden" : group.showAvailable || group.showMargin ? "block" : "col-span-2 flex items-center justify-between"} min-w-0 p-0 tabular-nums md:table-cell md:p-2 md:text-right`}
                            >
                              {!currentDuplicatesMargin(a) && (
                                <>
                                  <span className={`${group.showAvailable || group.showMargin ? "mb-0.5 block" : ""} text-xs text-muted-foreground md:hidden`}>
                                    Current value
                                  </span>
                                  <span className="block whitespace-nowrap">
                                    {money(a.current_value, a.currency)}
                                  </span>
                                </>
                              )}
                            </TableCell>
                          ) : (
                            <TableCell className="hidden md:table-cell" aria-hidden="true" />
                          )}
                          {group.showAvailable ? (
                            <TableCell className={`${a.available_value == null ? "hidden" : "block"} min-w-0 p-0 text-right tabular-nums md:table-cell md:p-2`}>
                              <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">
                                Available
                              </span>
                              <span className="block whitespace-nowrap">
                                {money(a.available_value, a.currency)}
                              </span>
                            </TableCell>
                          ) : (
                            <TableCell className="hidden md:table-cell" aria-hidden="true" />
                          )}
                          {group.showMargin ? (
                            <TableCell className={`${a.margin_balance == null ? "hidden" : "col-span-2 flex"} items-center justify-between gap-2 p-0 text-right tabular-nums md:table-cell md:p-2`}>
                              <span className="text-xs text-muted-foreground md:hidden">
                                Margin
                              </span>
                              <span className="whitespace-nowrap">
                                {money(a.margin_balance, a.currency)}
                                {a.margin_balance_source === "estimated" && (
                                  <span className="margin-source ml-1 text-xs text-muted-foreground">
                                    (est.)
                                  </span>
                                )}
                              </span>
                            </TableCell>
                          ) : (
                            <TableCell className="hidden md:table-cell" aria-hidden="true" />
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </section>
              ))}
            </div>

            {summary.debts.length > 0 && (
              <div className="brokerage-debts space-y-2" data-testid="brokerage-debts">
                <h4 className="text-sm font-semibold">Linked debts</h4>
                <div className="rounded-md border border-border">
                  <Table className="block md:table">
                    <TableHeader className="hidden md:table-header-group">
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Balance</TableHead>
                        <TableHead>Last statement</TableHead>
                        <TableHead>APR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody className="block divide-y divide-border md:table-row-group">
                      {summary.debts.map((d) => (
                        <TableRow key={d.account_id} data-testid="debt-row" className="grid grid-cols-2 gap-x-4 gap-y-3 p-3 md:table-row md:p-0">
                          <TableCell className="col-span-2 block p-0 font-medium md:table-cell md:p-2">{d.name}</TableCell>
                          <TableCell className="block min-w-0 p-0 md:table-cell md:p-2">
                            <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">Type</span>
                            <span className="block truncate">{d.subtype ?? d.account_type}</span>
                          </TableCell>
                          <TableCell className="block min-w-0 p-0 text-right tabular-nums md:table-cell md:p-2 md:text-left">
                            <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">Balance</span>
                            <span className="block whitespace-nowrap">{money(d.current_balance, d.currency)}</span>
                          </TableCell>
                          <TableCell className="block min-w-0 p-0 tabular-nums md:table-cell md:p-2">
                            <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">Last statement</span>
                            <span className="block whitespace-nowrap">{money(d.last_statement_balance, d.currency)}</span>
                          </TableCell>
                          <TableCell className="block min-w-0 p-0 text-right tabular-nums md:table-cell md:p-2 md:text-left">
                            <span className="mb-0.5 block text-xs text-muted-foreground md:hidden">APR</span>
                            <span className="block whitespace-nowrap">{percent(d.apr_percentage)}</span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {summary.source === "plaid" && (
              <>
              <HoldingsSection
                holdings={summary.holdings}
                allocation={summary.allocation}
                loading={refreshing}
              />
              {!!summary.holdings?.length && <PortfolioBenchmarks holdings={summary.holdings} />}
              </>
            )}

            <Button
              type="button"
              variant="outline"
              disabled={refreshing}
              onClick={async () => {
                setRefreshing(true);
                try {
                  await loadAccounts();
                } finally {
                  setRefreshing(false);
                }
              }}
            >
              {refreshing ? "Refreshing…" : "Refresh accounts"}
            </Button>
          </CardContent>
        </Card>
      )}
    </section>
  );
}
